import React, { useEffect, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import {
  faCircleCheck,
  faCircleXmark,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Popover, Stack } from "@mantine/core";

import { fetchScan } from "@api";
import { Scan } from "@types";

import { getScanStatus } from "@pages/dashboard/utils";
import { Repo } from "@pages/projects/types";

const DOCS_NEVER_FINISHED =
  "https://semgrep.dev/docs/troubleshooting/semgrep-app/#if-a-project-reports-the-last-scan-never-finished";
const DOCS_NEVER_STARTED =
  "https://semgrep.dev/docs/troubleshooting/semgrep-app/#if-a-project-reports-the-last-scan-never-started";
const DOCS_EXIT_CODES = "https://semgrep.dev/docs/cli-reference/#exit-codes";
const SUPPORT_URL = "https://semgrep.dev/docs/support/";
interface Props {
  repo: Repo;
}

const ErrorLink = styled.a`
  && {
    color: var(--global-red);
    text-decoration: underline;
  }

  &&:hover {
    color: var(--global-red);
    text-decoration: underline;
  }
`;

const StatusCell: React.FC<{
  label: string;
  intent: "success" | "failure" | "loading";
  showErrorLink?: boolean;
}> = ({ label, intent, showErrorLink, children }) => {
  const [icon, color] =
    intent === "success"
      ? [faCircleCheck, "var(--global-green)"]
      : intent === "failure"
      ? [faCircleXmark, "var(--global-red)"]
      : [faSpinner, "var(--global-dark-purple)"];

  return (
    <>
      <FontAwesomeIcon
        icon={icon}
        color={color}
        pulse={intent === "loading" ? true : undefined}
      />{" "}
      {label}
      {showErrorLink && (
        <>
          &nbsp;
          <Popover position="bottom" withArrow width={380}>
            <Popover.Dropdown>{children}</Popover.Dropdown>
            <Popover.Target>
              <ErrorLink>(why?)</ErrorLink>
            </Popover.Target>
          </Popover>
        </>
      )}
    </>
  );
};

export const LastScanCell: React.FC<Props> = ({ repo }) => {
  const [lastScan, setLastScan] = useState<Scan | null>(null);
  const [scanLoaded, setScanLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (repo === undefined) {
      // repo has not loaded yet
      return;
    } else if (!repo.latest_scan_id) {
      // repo loaded, but no scan to load
      setScanLoaded(true);
      return;
    }

    fetchScan(repo.latest_scan_id).then((response) => {
      setLastScan(response.scan);
      setScanLoaded(true);
    });
  }, [repo]);

  const scanStatus = getScanStatus(repo, lastScan);

  if (repo === undefined || !scanLoaded) {
    return <div>loading scan...</div>;
  }

  switch (scanStatus) {
    case "starting": {
      return <StatusCell intent="loading" label="Starting" />;
    }
    case "never_started": {
      return (
        <StatusCell intent="failure" label="Never Started" showErrorLink>
          The scan never started after the CI job was installed. It's likely
          that something is wrong with the CI configuration or the secret. Visit{" "}
          <a
            href={DOCS_NEVER_STARTED}
            target="_blank"
            rel="noopener noreferrer"
          >
            this page
          </a>{" "}
          in our documentation for more information.
          <br />
          <br />
          If you get stuck or want help,{" "}
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            reach out to us
          </a>
          {"."}
        </StatusCell>
      );
    }
    case "running": {
      return <StatusCell intent="loading" label={`In Progress`} />;
    }
    case "succeeded": {
      const completedTime = moment(lastScan!.completed_at);
      return (
        <StatusCell
          intent="success"
          label={`Finished ${completedTime.fromNow()}`}
        />
      );
    }
    case "error": {
      const completedTime = moment(lastScan!.completed_at);
      return (
        <StatusCell
          intent="failure"
          label={`Failed ${completedTime.fromNow()}`}
          showErrorLink
        >
          <Stack>
            <div>
              The scan ran to completion, but encountered an internal error
              (exit code <code>{lastScan!.exit_code}</code>). Visit{" "}
              <a
                href={DOCS_EXIT_CODES}
                target="_blank"
                rel="noopener noreferrer"
              >
                this page
              </a>{" "}
              in our documentation for more information, or{" "}
              <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                reach out to us
              </a>{" "}
              for more support.
            </div>
            {lastScan!.meta?.ci_job_url && (
              <Button
                variant="light"
                color="gray"
                component="a"
                href={lastScan?.meta.ci_job_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View CI Job
              </Button>
            )}
          </Stack>
        </StatusCell>
      );
    }
    case "never_finished": {
      return (
        <StatusCell intent="failure" label="Never Finished" showErrorLink>
          <Stack>
            <div>
              The scan started, but we never heard back with results. It's
              likely that the CI job was cancelled. See{" "}
              <a
                href={DOCS_NEVER_FINISHED}
                target="_blank"
                rel="noopener noreferrer"
              >
                this page
              </a>{" "}
              for more information and suggestions.
            </div>
            <div>
              If you get stuck or want help,{" "}
              <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                reach out to us
              </a>
              {"."}
            </div>
            <div>
              {lastScan!.meta?.ci_job_url && (
                <Button
                  variant="light"
                  color="gray"
                  component="a"
                  href={lastScan!.meta.ci_job_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View CI Job
                </Button>
              )}
            </div>
          </Stack>
        </StatusCell>
      );
    }
  }
};
